import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, Input, ViewChild} from "@angular/core";
import {MatSelect, MatSelectModule} from "@angular/material/select";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {MatFormFieldControl} from "@angular/material/form-field";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {MatFormFieldControlProxy} from "web-ng2/delt/material/MatFormFieldControlProxy";
import {Subject} from "rxjs";
import {Temporal} from "temporal-polyfill";
import {mapBasertPåIndeks, nyArray} from "felles-ng2/støttefunksjoner/lister";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {MøteDato} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {TranslateModule} from "@ngx-translate/core";
import {lesZonedDateTimeSerialisert} from "felles-ng2/støttefunksjoner/temporal";
import {møtevarighetIAntallDager} from "felles-ng2/modell/møteModellHjelpere";
import PlainDate = Temporal.PlainDate;
import ZonedDateTime = Temporal.ZonedDateTime;

const debug = initDebugger('VelgDagFraFlerdagsmøteKomponent');


/** Viser nedtrekksmeny som lar bruker velge blant dagene som et flerdagsmøte spenner over.
 *
 * <pre>
 * Fungerer med Ng reactive form via ([(skjemakontroll)]="...").
 * Fungerer med <mat-form-field> fra Material.
 *
 * MERK at vi ikke implementerer ControlValueAccessor fra angular.forms, så komponenten vil ikke fungere med
 * [(ngModel)]="..." eller [formControl]="...".
 * </pre>
 */
@Component({
    selector: 'velg-dag-fra-flerdagsmoete',
    templateUrl: './velg-dag-fra-flerdagsmøte.html',
    styleUrls: ['./velg-dag-fra-flerdagsmøte.scss'],
    providers: [{
        provide: MatFormFieldControl,
        useFactory: (komponent: VelgDagFraFlerdagsmøteKomponent) => new MatFormFieldControlProxy(() => komponent.nedtrekksmeny, komponent.stateChanges),
        deps: [VelgDagFraFlerdagsmøteKomponent]
    }],
    imports: [
        MatSelectModule,
        ReactiveFormsModule,
        FormaterPlainDatePipe,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VelgDagFraFlerdagsmøteKomponent implements AfterViewInit {

    @ViewChild('dagvelger') private nedtrekksmeny!: MatSelect;

    @Input({required: true}) set moetedato(møtedato: MøteDato) {
        this.setMøtedato(møtedato);
    }
    @Input({required: true}) skjemakontroll!: FormControl<number | null>;
    @Input() placeholder = '';

    dagerVedFlerdagsmoeter: DagModell[] = [];

    private readonly stateChanges = new Subject<void>();

    constructor(private destroyRef: DestroyRef) {
    }

    valgtDagSomDato(): PlainDate | null {
        return this.dagerVedFlerdagsmoeter.find(kandidat => kandidat.verdi === this.skjemakontroll.value)?.dato ?? null;
    }

    erValgt(a: number, b: number): boolean {
        return a === b;
    }

    ngAfterViewInit(): void {
        this.nedtrekksmeny.stateChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.stateChanges.next())
    }

    //hjelpemetoder

    private setMøtedato(møtedato: MøteDato) {
        this.dagerVedFlerdagsmoeter = this.dagModeller(møtedato);
    }

    private dagModeller(dato: MøteDato): DagModell[] {
        const modeller = nyArray(møtevarighetIAntallDager(dato))
          .map(mapBasertPåIndeks(indeks => this.dagModell(indeks, lesZonedDateTimeSerialisert(dato.start))))
        return modeller;
    }

    /** Returnerer ny DagModell, basert på angitt dag (0-basert) relativ til angitt start, der dag 0 representerer samme dag som angitt start. */
    private dagModell(dag: number, start: ZonedDateTime): DagModell {
        return {
            verdi: dag,
            dato: start.toPlainDate().add({days: dag}),
        };
    }
}


interface DagModell {
    verdi: number;
    dato: PlainDate;
}
